import { useEffect, useState } from "react";
import axios from "axios";
import { Search, Lock, BadgeCheck, MapPin, CalendarClock, Send, Loader2, X, Mail } from "lucide-react";
import { useSkillCatalog, SkillChips } from "./SkillCatalog";
import { SEARCH_T, TL } from "../content/talentI18n";
import ProfLevelFilter from "./ProfLevelFilter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full bg-black border border-white/15 px-3 py-2.5 text-sm text-white focus:border-[#D4AF37] outline-none";

export const ON_REQ = { sk: "Dostupnosť na vyžiadanie", en: "Availability on request", de: "Verfügbarkeit auf Anfrage", pl: "Dostępność na zapytanie", fr: "Disponibilité sur demande" };
const VIA = {
  sk: ["Odoslať cez SauberEko®", "Ponuku sme prijali – sprostredkujeme ju profesionálovi."],
  en: ["Send via SauberEko®", "Offer received – we will forward it to the professional."],
  de: ["Über SauberEko® senden", "Angebot erhalten – wir leiten es an die Fachkraft weiter."],
  pl: ["Wyślij przez SauberEko®", "Oferta przyjęta – przekażemy ją fachowcowi."],
  fr: ["Envoyer via SauberEko®", "Offre reçue – nous la transmettrons au professionnel."],
};
const GRANTED = { sk: "Máte bezplatný plný prístup pridelený SauberEko®", en: "You have free full access granted by SauberEko®", de: "Sie haben einen kostenlosen Vollzugang von SauberEko®", pl: "Masz bezpłatny pełny dostęp przyznany przez SauberEko®", fr: "Vous disposez d'un accès complet gratuit accordé par SauberEko®" };

export function ContactModal({ S: S0, cand, onSend, onClose, lang = "sk" }) {
  const via = cand.contact?.startsWith("cert:") ? (VIA[lang] || VIA.sk) : null;
  const S = via ? { ...S0, send: via[0], sent: via[1] } : S0;
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const send = async () => {
    setBusy(true); setMsg("");
    try { await onSend(subject, text); setMsg(S.sent); setTimeout(onClose, 1500); }
    catch (e) { setMsg(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid="contact-modal">
      <div className="w-full max-w-lg border border-[#D4AF37]/40 bg-[#0b0b0b] p-6">
        <div className="flex items-start justify-between mb-4">
          <div><p className="font-display text-xl">{cand.name}</p><p className="text-[#D4AF37] text-xs font-mono">{cand.certificate_no}</p></div>
          <button onClick={onClose} data-testid="contact-close" className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={S.subject} className={`${inp} mb-3`} data-testid="contact-subject" />
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} placeholder={S.text} className={inp} data-testid="contact-text" />
        <div className="flex items-center gap-3 mt-4">
          <button onClick={send} disabled={busy || text.trim().length < 5} data-testid="contact-send"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm hover:bg-[#F2E3C6] disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {S.send}
          </button>
          <button onClick={onClose} className="border border-white/15 px-4 py-2.5 text-sm text-white/60">{S.cancel}</button>
        </div>
        {msg && <p className="text-[#D4AF37] text-sm mt-3" data-testid="contact-msg">{msg}</p>}
      </div>
    </div>
  );
}

const UV = {
  sk: { t: "Potvrďte e-mail firmy", d: "Na adresu {e} sme poslali potvrdzovací odkaz. Prístup do databázy kandidátov sa sprístupní až po potvrdení e-mailu.", b: "Poslať odkaz znova", s: "Odkaz sme poslali znova.", ok: "E-mail bol úspešne potvrdený.", bad: "Odkaz je neplatný alebo už bol použitý." },
  en: { t: "Confirm your company e-mail", d: "We sent a confirmation link to {e}. Access to the candidate database is unlocked once the e-mail is confirmed.", b: "Send the link again", s: "The link has been sent again.", ok: "Your e-mail has been confirmed.", bad: "The link is invalid or has already been used." },
  de: { t: "Firmen-E-Mail bestätigen", d: "Wir haben einen Bestätigungslink an {e} gesendet. Der Zugang zur Kandidatendatenbank wird nach der Bestätigung freigeschaltet.", b: "Link erneut senden", s: "Der Link wurde erneut gesendet.", ok: "Ihre E-Mail wurde bestätigt.", bad: "Der Link ist ungültig oder wurde bereits verwendet." },
  pl: { t: "Potwierdź e-mail firmy", d: "Wysłaliśmy link potwierdzający na adres {e}. Dostęp do bazy kandydatów zostanie odblokowany po potwierdzeniu e-maila.", b: "Wyślij link ponownie", s: "Link został wysłany ponownie.", ok: "E-mail został potwierdzony.", bad: "Link jest nieprawidłowy lub został już użyty." },
};

function Unverified({ email, auth, U }) {
  const [msg, setMsg] = useState("");
  const [pre, post] = U.d.split("{e}");
  const resend = async () => {
    try { await axios.post(`${API}/applicant/verify-email/resend`, {}, auth()); setMsg(U.s); }
    catch (e) { setMsg(e?.response?.data?.detail || "Error"); }
  };
  return (
    <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6" data-testid="search-unverified">
      <h3 className="font-display text-xl flex items-center gap-2"><Mail size={18} className="text-[#D4AF37]" /> {U.t}</h3>
      <p className="text-white/60 text-sm mt-2 mb-5">{pre}<strong className="text-white">{email}</strong>{post}</p>
      <button onClick={resend} data-testid="search-verify-resend" className="border border-[#D4AF37] text-[#D4AF37] px-5 py-2.5 text-sm hover:bg-[#D4AF37] hover:text-black transition-colors">{U.b}</button>
      {msg && <p className="text-[#D4AF37] text-sm mt-3" data-testid="search-verify-msg">{msg}</p>}
    </div>
  );
}

function Paywall({ S, auth }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pay = async () => {
    setBusy(true); setErr("");
    try { const { data } = await axios.post(`${API}/company/search-access/checkout`, { origin_url: window.location.origin }, auth()); window.location.href = data.url; }
    catch (e) { setErr(e?.response?.data?.detail || "Error"); setBusy(false); }
  };
  return (
    <div className="border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-6" data-testid="search-paywall">
      <h3 className="font-display text-xl flex items-center gap-2"><Lock size={18} className="text-[#D4AF37]" /> {S.locked}</h3>
      <p className="text-white/60 text-sm mt-2 mb-5">{S.lockedD}</p>
      <button onClick={pay} disabled={busy} data-testid="search-pay" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-3 hover:bg-[#F2E3C6] disabled:opacity-60">
        {busy && <Loader2 size={15} className="animate-spin" />} {S.pay}
      </button>
      {err && <p className="text-red-400 text-sm mt-3">{err}</p>}
    </div>
  );
}

function CandidateCard({ S, L, c, labels, onContact, lang }) {
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-5 hover:border-[#D4AF37]/40 transition-colors" data-testid={`candidate-${c.contact}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-display text-lg">{c.name}</p>
          <p className="text-xs mt-1 flex flex-wrap gap-x-4 gap-y-1 text-white/50">
            <span className="inline-flex items-center gap-1 text-[#D4AF37]"><BadgeCheck size={13} /> {c.kind === "certificate" ? L.docCert : L.docVer} · <span className="font-mono">{c.certificate_no}</span></span>
            {c.city && <span className="inline-flex items-center gap-1"><MapPin size={13} /> {c.city}</span>}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className={`inline-flex items-center gap-1.5 text-sm ${c.available ? "text-emerald-300" : "text-white/45"}`} data-testid={`candidate-available-${c.contact}`}><CalendarClock size={14} />
            {!c.available ? (ON_REQ[lang] || ON_REQ.sk) : c.available_from <= today ? S.now : `${S.availFrom} ${new Date(c.available_from).toLocaleDateString("sk-SK")}`}</p>
          <div><button onClick={() => onContact(c)} data-testid={`candidate-contact-${c.contact}`} className="mt-2 inline-flex items-center gap-1.5 border border-[#D4AF37] text-[#D4AF37] px-4 py-1.5 text-sm hover:bg-[#D4AF37] hover:text-black transition-colors"><Send size={13} /> {S.contact}</button></div>
        </div>
      </div>
      <div className="mt-3"><SkillChips ids={c.skills} labels={labels} /></div>
    </div>
  );
}

export default function CandidateSearch({ lang, auth }) {
  const S = SEARCH_T[lang] || SEARCH_T.sk;
  const L = TL[lang] || TL.sk;
  const U = UV[lang] || UV.sk;
  const [ev] = useState(() => new URLSearchParams(window.location.search).get("email_verified"));
  const { groups, labels } = useSkillCatalog(lang);
  const [access, setAccess] = useState(null);
  const [sel, setSel] = useState(() => (new URLSearchParams(window.location.search).get("skills") || "").split(",").filter(Boolean));
  const [res, setRes] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [contact, setContact] = useState(null);
  const [pf, setPf] = useState(() => { const q = new URLSearchParams(window.location.search); return { prof: q.get("prof") || "", level: q.get("level") || "" }; });

  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    const load = () => axios.get(`${API}/company/search-access`, auth()).then(({ data }) => setAccess(data)).catch(() => setAccess({ access: false }));
    if (!sid) { load(); return; }
    setAccess({ verifying: true });
    let n = 0;
    const poll = async () => {
      n += 1;
      try { const { data } = await axios.get(`${API}/applicant/payment/status/${sid}`, auth()); if (data.payment_status === "paid" || n > 6) { window.history.replaceState({}, "", "/ziadost?tab=search"); return load(); } } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const search = async () => {
    setBusy(true); setErr("");
    try { const { data } = await axios.get(`${API}/company/candidates`, { ...auth(), params: { skills: sel.join(","), prof: pf.prof, level: pf.level } }); setRes(data.results); }
    catch (e) { setRes(null); setErr(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  if (!access || access.verifying) return <p className="text-white/50 text-sm flex items-center gap-2"><Loader2 size={15} className="animate-spin" /> {access?.verifying ? S.verifying : ""}</p>;
  if (access.reason === "not_company") return <p className="text-white/50 text-sm">{S.onlyCompany}</p>;
  return (
    <div data-testid="candidate-search">
      <h2 className="font-display text-2xl flex items-center gap-2 mb-1"><Search size={20} className="text-[#D4AF37]" /> {S.title}</h2>
      <p className="text-white/45 text-sm mb-6">{S.hint}</p>
      {ev && <p className={`text-sm mb-5 ${ev === "1" ? "text-emerald-300" : "text-red-300"}`} data-testid="email-verified-msg">{ev === "1" ? U.ok : U.bad}</p>}
      {access.reason === "unverified" ? <Unverified email={access.email} auth={auth} U={U} /> : !access.access ? <Paywall S={S} auth={auth} /> : (
        <>
          <p className="text-emerald-300/90 text-sm mb-6 flex items-center gap-2" data-testid="search-access-ok"><BadgeCheck size={15} /> {access.reason === "certified" ? S.free : access.reason === "granted" ? (GRANTED[lang] || GRANTED.sk) : S.paid}{access.until ? ` · ${new Date(access.until).toLocaleString("sk-SK", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}</p>
          <div className="border border-white/10 bg-[#0a0a0a] p-5 space-y-5">
            <ProfLevelFilter lang={lang} value={pf} onChange={setPf} testid="search-prof-filter" />
            {groups.map((g) => (
              <div key={g.id}>
                <p className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37]/80 mb-2">{g.label}</p>
                <div className="flex flex-wrap gap-2">
                  {g.skills.map((s) => (
                    <button key={s.id} type="button" onClick={() => toggle(s.id)} data-testid={`skill-filter-${s.id}`}
                      className={`text-xs px-3 py-1.5 border transition-colors ${sel.includes(s.id) ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/70 hover:border-[#D4AF37]/60"}`}>{s.label}</button>
                  ))}
                </div>
              </div>
            ))}
            <div className="flex gap-3 pt-2">
              <button onClick={search} disabled={busy} data-testid="search-submit" className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-6 py-2.5 text-sm hover:bg-[#F2E3C6] disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} {S.search}
              </button>
              {sel.length > 0 && <button onClick={() => { setSel([]); setRes(null); }} data-testid="search-clear" className="border border-white/15 px-4 py-2.5 text-sm text-white/60 hover:text-white">{S.clear}</button>}
            </div>
          </div>
          {err && <p className="text-red-400 text-sm mt-4" data-testid="search-error">{err}</p>}
          {res && (
            <div className="mt-6 space-y-3" data-testid="search-results">
              <p className="text-white/45 text-sm">{S.found}: {res.length}</p>
              {res.length === 0 && <p className="text-white/50 text-sm" data-testid="search-empty">{S.none}</p>}
              {res.map((c) => <CandidateCard key={c.contact} S={S} L={L} c={c} labels={labels} onContact={setContact} lang={lang} />)}
            </div>
          )}
        </>
      )}
      {contact && <ContactModal S={S} cand={contact} onSend={(subject, text) => axios.post(`${API}/company/candidates/${encodeURIComponent(contact.contact)}/message`, { subject, text }, auth())} onClose={() => setContact(null)} lang={lang} />}
    </div>
  );
}
